'use client';

import { cn } from '@/shared/lib/cn';
import type { AspectRatioConfig } from '@/shared/constants/aspect-ratios';

interface AspectRatioChipProps {
  config: AspectRatioConfig;
  isSelected: boolean;
  onClick: () => void;
  className?: string;
}

/**
 * Visual chip showing aspect ratio with proportional icon
 */
export function AspectRatioChip({
  config,
  isSelected,
  onClick,
  className,
}: AspectRatioChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors min-w-[56px]',
        'hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isSelected
          ? 'bg-primary/10 border-2 border-primary'
          : 'bg-muted/50 border-2 border-transparent',
        className
      )}
    >
      {/* Show ratio icon only for the selected chip — keeps the row compact so
          all ratios fit without horizontal scrolling on smaller screens. */}
      {isSelected && <AspectRatioIcon ratio={config.ratio} />}
      <span
        className={cn(
          'text-chip whitespace-nowrap',
          isSelected ? 'text-primary' : 'text-muted-foreground'
        )}
      >
        {config.label}
      </span>
    </button>
  );
}

interface AspectRatioIconProps {
  ratio: number;
}

/**
 * Visual icon showing the aspect ratio as a proportional rectangle.
 * Only rendered for the selected chip — uses primary color against the
 * selected chip's tinted background.
 */
function AspectRatioIcon({ ratio }: AspectRatioIconProps) {
  // Calculate dimensions to fit within a 24x24 box
  const maxSize = 24;
  let width: number;
  let height: number;

  if (ratio >= 1) {
    // Horizontal or square
    width = maxSize;
    height = maxSize / ratio;
  } else {
    // Vertical
    height = maxSize;
    width = maxSize * ratio;
  }

  // Ensure minimum size
  width = Math.max(width, 8);
  height = Math.max(height, 8);

  return (
    <svg
      width={maxSize}
      height={maxSize}
      viewBox={`0 0 ${maxSize} ${maxSize}`}
      className="flex-shrink-0"
    >
      <rect
        x={(maxSize - width) / 2}
        y={(maxSize - height) / 2}
        width={width}
        height={height}
        rx={2}
        className="stroke-2 fill-none stroke-primary"
      />
    </svg>
  );
}

interface FreeAspectRatioChipProps {
  isSelected: boolean;
  onClick: () => void;
  label: string;
  className?: string;
}

/**
 * Special chip for free (unconstrained) aspect ratio
 */
export function FreeAspectRatioChip({
  isSelected,
  onClick,
  label,
  className,
}: FreeAspectRatioChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors min-w-[56px]',
        'hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isSelected
          ? 'bg-primary/10 border-2 border-primary'
          : 'bg-muted/50 border-2 border-transparent',
        className
      )}
    >
      {/* Match AspectRatioChip behavior — icon only when selected. */}
      {isSelected && <FreeAspectRatioIcon />}
      <span
        className={cn(
          'text-chip whitespace-nowrap',
          isSelected ? 'text-primary' : 'text-muted-foreground'
        )}
      >
        {label}
      </span>
    </button>
  );
}

function FreeAspectRatioIcon() {
  const maxSize = 24;

  return (
    <svg
      width={maxSize}
      height={maxSize}
      viewBox={`0 0 ${maxSize} ${maxSize}`}
      className="flex-shrink-0"
    >
      {/* Dashed rectangle indicating free/flexible aspect ratio */}
      <rect
        x={2}
        y={2}
        width={20}
        height={20}
        rx={2}
        className="stroke-2 fill-none stroke-primary"
        strokeDasharray="4 2"
      />
      {/* Diagonal arrows indicating expandability */}
      <path
        d="M8 8L4 4M16 16L20 20M8 16L4 20M16 8L20 4"
        className="stroke-[1.5] fill-none stroke-primary"
        strokeLinecap="round"
      />
    </svg>
  );
}
